import Layout from "@/components/site/Layout";
import Seo from "@/components/site/Seo";
import { Link } from "react-router-dom";
import { CITIES } from "@/config/cities";
import { PHONE_DISPLAY, PHONE_TEL, getSiteUrl } from "@/config/site";
import { breadcrumbSchema } from "@/lib/schema";
import { MapPin, Phone, ArrowRight, Clock3, ShieldCheck } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

// Preferred display order — most-searched cities first
const CITY_ORDER = [
  "bellevue",
  "kirkland",
  "redmond",
  "bothell",
  "woodinville",
  "sammamish",
  "issaquah",
  "mercer-island",
  "newcastle",
];

export default function CitiesPage() {
  const site = getSiteUrl();
  const url = `${site}/cities`;
  const headRef = useReveal();
  const gridRef = useReveal(0.02);

  const cities = CITY_ORDER.map((slug) => CITIES[slug]).filter(Boolean);

  const jsonLd = [
    breadcrumbSchema([
      { name: "Home", url: `${site}/` },
      { name: "Cities We Serve", url },
    ]),
    // ItemList of city landing pages
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: cities.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `Tow Truck in ${c.name}, WA`,
        url: `${site}${c.path}`,
      })),
    },
  ];

  return (
    <Layout>
      <Seo
        title={`Cities We Serve on the Eastside | BM Towing`}
        description={`BM Towing & Recovery serves ${cities.map((c) => c.name).join(", ")} — 24/7 flatbed towing, roadside assistance, jump starts, lockouts, and equipment transport. Call ${PHONE_DISPLAY} for immediate dispatch.`}
        canonical={url}
        ogImage="/images/hero-porsche.webp"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        className="relative bg-black overflow-hidden border-b border-yellow-400/30"
        data-testid="cities-hero"
      >
        <div className="pointer-events-none absolute inset-0 bm-grain" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-40"
          style={{ background: "radial-gradient(circle, rgba(250,204,21,0.30) 0%, rgba(0,0,0,0) 60%)" }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -left-32 w-[560px] h-[560px] rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, rgba(59,130,246,0.30) 0%, rgba(0,0,0,0) 60%)" }}
        />

        <div ref={headRef} className="lux-reveal relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-14 sm:pb-16">
          <nav aria-label="Breadcrumb" className="text-xs text-zinc-500 font-display uppercase tracking-[0.28em]">
            <Link to="/" className="hover:text-yellow-400 transition-colors">Home</Link>
            <span className="mx-2 text-zinc-700">/</span>
            <span className="text-white">Cities We Serve</span>
          </nav>

          <div className="mt-6 inline-flex items-center gap-2 lux-glass rounded-full px-3 py-1.5">
            <MapPin className="h-3.5 w-3.5 text-yellow-400" aria-hidden="true" strokeWidth={3} />
            <span className="font-display uppercase tracking-[0.25em] text-[10px] text-white/90">
              Proudly Serving the Eastside
            </span>
          </div>

          <h1 className="mt-4 font-display font-black uppercase text-white leading-[0.92] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            Cities we <span className="text-yellow-400">serve.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-zinc-400 text-base sm:text-lg font-light leading-relaxed">
            BM Towing &amp; Recovery is a mobile, service-area business — no yard, no shop, no queue. We dispatch a flatbed or roadside truck from our Kirkland base to every city on this list, 24 hours a day, 7 days a week. Tap your city for local content, or just call us — we'll be moving before you hang up.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="cities-hero-call"
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-3.5 rounded-full lux-glow-yellow transition-colors"
            >
              <Phone className="h-5 w-5" strokeWidth={3} /> Call Now: {PHONE_DISPLAY}
            </a>
            <a
              href="#city-list"
              className="inline-flex items-center gap-2 lux-glass hover:border-yellow-400/50 text-white font-display font-bold uppercase tracking-wider px-6 py-3.5 rounded-full transition-colors"
            >
              Browse Cities <ArrowRight className="h-4 w-4" strokeWidth={3} />
            </a>
          </div>
        </div>
      </section>

      {/* CITY GRID */}
      <section id="city-list" ref={gridRef} className="lux-reveal relative bg-[#0A0A0A] py-24 sm:py-32 overflow-hidden border-t border-white/5">
        <div className="pointer-events-none absolute inset-0 bm-grain" />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(250,204,21,0.12) 0%, rgba(0,0,0,0) 55%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
            <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
            Service Areas
          </div>
          <h2 className="mt-3 font-display font-black uppercase text-white text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight max-w-3xl">
            Choose your city — <span className="text-yellow-400">we're already close.</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {cities.map((c, i) => (
              <Link
                key={c.slug}
                to={c.path}
                data-testid={`cities-card-${c.slug}`}
                className={`group lux-card lux-glass rounded-2xl p-6 flex flex-col gap-4 relative overflow-hidden lux-reveal lux-reveal-delay-${(i % 4) + 1}`}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-0 group-hover:opacity-60 transition-opacity duration-500"
                  style={{ background: "radial-gradient(circle, rgba(250,204,21,0.35) 0%, rgba(0,0,0,0) 70%)" }}
                />
                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display uppercase tracking-[0.28em] text-[10px] text-yellow-400">
                      Tow Truck In
                    </div>
                    <div className="mt-1 font-display font-black uppercase text-white text-2xl sm:text-3xl leading-none tracking-tight">
                      {c.name}, WA
                    </div>
                  </div>
                  <div className="h-11 w-11 shrink-0 rounded-xl bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-yellow-400" strokeWidth={2} />
                  </div>
                </div>

                <p className="relative text-zinc-400 text-sm font-light leading-relaxed">
                  {c.hero}
                </p>

                <div className="relative flex flex-wrap gap-1.5 mt-1">
                  {c.highways.slice(0, 3).map((h) => (
                    <span
                      key={h}
                      className="text-[10px] font-display uppercase tracking-widest lux-glass rounded-full text-yellow-400 border border-yellow-400/30 px-2.5 py-1"
                    >
                      {h}
                    </span>
                  ))}
                </div>

                <div className="relative mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
                  <span className="font-display uppercase tracking-[0.25em] text-[10px] text-zinc-500">
                    24/7 Dispatch
                  </span>
                  <span className="inline-flex items-center gap-1 font-display uppercase tracking-widest text-xs text-yellow-400 group-hover:text-yellow-300 transition-colors">
                    View {c.name} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" strokeWidth={3} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Trust rail */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Clock3, title: "24/7 Dispatch", desc: "Real people. Every hour. Every day." },
              { icon: ShieldCheck, title: "Licensed & Insured", desc: "Trained flatbed & roadside operators." },
              { icon: MapPin, title: "Mobile Fleet", desc: "No yard — we come to you." },
            ].map((t) => (
              <div key={t.title} className="lux-glass rounded-xl p-5 flex items-start gap-3">
                <div className="h-11 w-11 rounded-lg bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center shrink-0">
                  <t.icon className="h-5 w-5 text-yellow-400" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-display font-black uppercase text-white text-sm">{t.title}</div>
                  <div className="text-zinc-400 text-xs mt-0.5 font-light">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative bg-black py-20 sm:py-24 border-t border-white/5 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(250,204,21,0.15) 0%, rgba(0,0,0,0) 55%)" }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs">
            Stranded right now?
          </div>
          <h2 className="mt-3 font-display font-black uppercase text-white text-3xl sm:text-4xl lg:text-5xl leading-[0.95] tracking-tight">
            Don't scroll — <span className="text-yellow-400">call.</span>
          </h2>
          <p className="mt-4 text-zinc-400 text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto">
            One phone call gets a driver moving toward you. We quote the price up-front and we don't leave you waiting.
          </p>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="cities-bottom-call"
            className="mt-8 inline-flex items-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-7 py-4 rounded-full lux-glow-yellow transition-colors text-base"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> Call Now: {PHONE_DISPLAY}
          </a>
        </div>
      </section>
    </Layout>
  );
}
